import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./styles/Cashier.css";

// Import components
import Approval from "./Approval";
import MakeOrder from "./Homee";
import Report from "./Printing";

const PAYMENT_API = "https://salathai.co.tz/mrms/Payment.php";

export default function Cashier() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const staff = JSON.parse(localStorage.getItem("staff") || "{}");
  
  // State for active navigation
  const [activeNav, setActiveNav] = useState("Dashboard");
  const [stats, setStats] = useState({
    totalTransactionsToday: 0
  });

  useEffect(() => {
    if (!token) {
      Swal.fire("Session Expired", "Please login again", "warning");
      navigate("/");
      return;
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(PAYMENT_API, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const payments = await res.json();
        
        // Get today's date (start of day)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Filter transactions that were created today
        const todayTransactions = payments.filter(p => {
          const transactionDate = new Date(p.created_at || p.date || p.timestamp);
          transactionDate.setHours(0, 0, 0, 0);
          return transactionDate.getTime() === today.getTime();
        });

        setStats({
          totalTransactionsToday: todayTransactions.length
        });
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load statistics", "error");
    }
  };

  const handleLogout = async () => {
    const confirm = await Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to logout?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel"
    });

    if (confirm.isConfirmed) {
      localStorage.clear();
      navigate("/");
    }
  };

  // Navigation items configuration
  const navItems = [
    { id: "Dashboard", label: "Dashboard", icon: "" },
    { id: "Make Order", label: "Make Order", icon: "" },
    { id: "Payment Approval", label: "Payment Approval", icon: "" },
    { id: "Print Order", label: "Print Order", icon: "" }
  ];

  // Get page title based on activeNav
  const getPageTitle = () => {
    switch(activeNav) {
      case "Dashboard": return "Dashboard Overview";
      case "Make Order": return "Create New Order";
      case "Payment Approval": return "Review & Approve Payments";
      case "Print Order": return "Print Receipts & Reports";
      default: return "Dashboard";
    }
  };

  return (
    <div className="cashier-app">
      {/* Header */}
      <header className="cashier-header">
        <div className="header-container">
          {/* Logo Section */}
          <div className="logo-section">
            <div className="logo">
              <span className="logo-icon"></span>
              <span className="logo-text">CASHIER DASHBOARD</span>
            </div>
            
            {/* Quick Stats in Header */}
            <div className="header-stats">
              <div className="header-stat pending-stat">
                <span className="stat-icon"></span>
                <span className="stat-value">{stats.totalTransactionsToday}</span>
                <span className="stat-label">Today's Transactions</span>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <nav className="nav-menu">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveNav(item.id)}
                className={`nav-btn ${activeNav === item.id ? 'active' : ''}`}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}

            {/* User Info & Logout */}
            <div className="user-section">
              <div className="user-avatar">
                {staff.full_name?.charAt(0) || "U"}
              </div>
              <div className="user-details">
                <p className="user-name">{staff.full_name || "User"}</p>
                <p className="user-role">{staff.role || "Cashier"}</p>
              </div>
              <button 
                onClick={handleLogout} 
                className="logout-btn"
                title="Logout"
              >
                🚪
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="cashier-main">
        <div className="content-container">
          {/* Page Header */}
          <div className="page-header">
            <h1 className="page-title">{getPageTitle()}</h1>
            <p className="page-description">
              {activeNav === "Dashboard" && "Welcome back! Here's today's transaction summary."}
              {activeNav === "Make Order" && "Create new orders for customers. Items will be sent to the kitchen."}
              {activeNav === "Payment Approval" && "Review and approve pending payment requests from waiters."}
              {activeNav === "Print Order" && "Generate and print receipts for completed orders."}
            </p>
          </div>

          {/* Content Area - Conditional Rendering */}
          <div className="content-area">
            {/* DASHBOARD */}
            {activeNav === "Dashboard" && (
              <div className="dashboard-view">
                {/* Stats Grid - Only Today's Total */}
                <div className="stats-grid">
                  <div className="stat-card total-card">
                    <div className="stat-icon-wrapper total-icon">
                    
                    </div>
                    <div className="stat-info">
                      <h4 className="stat-title">Today's Transactions</h4>
                      <p className="stat-number">{stats.totalTransactionsToday}</p>
                      <span className="stat-action">Today's Total</span>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="quick-actions-section">
                  <h3 className="section-title">Quick Actions</h3>
                  <div className="actions-grid">
                    {[
                      { id: "Make Order", label: "Create Order", icon: "", color: "#10b981", desc: "Take new customer order" },
                      { id: "Payment Approval", label: "Approve Payments", icon: "", color: "#f59e0b", desc: "Review pending payments" },
                      { id: "Print Order", label: "Print Receipts", icon: "", color: "#3b82f6", desc: "Generate order receipts" },
                      { id: "refresh", label: "Refresh Data", icon: "", color: "#8b5cf6", desc: "Update statistics" }
                    ].map((action) => (
                      <button 
                        key={action.label}
                        onClick={() => {
                          if (action.id === "refresh") {
                            fetchStats();
                            Swal.fire("Refreshed", "Today's transaction count updated", "success", 1500);
                          } else {
                            setActiveNav(action.id);
                          }
                        }}
                        className="action-card"
                      >
                        <div className="action-icon" style={{ backgroundColor: `${action.color}15`, color: action.color }}>
                          {action.icon}
                        </div>
                        <div className="action-content">
                          <h4 className="action-title">{action.label}</h4>
                          <p className="action-desc">{action.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Welcome Message */}
                <div className="welcome-section">
                  <div className="welcome-card">
                    <div className="welcome-icon"></div>
                    <div className="welcome-content">
                      <h3 className="welcome-title">Welcome, {staff.full_name?.split(' ')[0] || "Cashier"}!</h3>
                      <p className="welcome-text">
                        You have processed {stats.totalTransactionsToday} transaction{stats.totalTransactionsToday !== 1 ? 's' : ''} today. Use the quick actions above to continue.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MAKE ORDER */}
            {activeNav === "Make Order" && (
              <div className="component-wrapper">
                <MakeOrder />
              </div>
            )}

            {/* PAYMENT APPROVAL */}
            {activeNav === "Payment Approval" && (
              <div className="component-wrapper">
                <Approval />
              </div>
            )}

            {/* PRINT ORDER */}
            {activeNav === "Print Order" && (
              <div className="component-wrapper">
                <Report />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}